import { clsx } from "clsx";
import { ArrowUpRight, Inbox, LoaderCircle, Plus } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export function Button({
  children,
  variant = "primary",
  size = "md",
  icon,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "icon";
  icon?: ReactNode;
}) {
  return (
    <button
      className={clsx("button", `button--${variant}`, `button--${size}`, className)}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
}

export function Card({
  children,
  className,
  interactive = false
}: {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
}) {
  return <section className={clsx("card", interactive && "card--interactive", className)}>{children}</section>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <div className="page-header__eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function SectionHeading({
  title,
  description,
  action
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatusPill({ status, children }: { status?: string; children: ReactNode }) {
  return (
    <span className={clsx("status-pill", status && `status-pill--${status.toLowerCase()}`)}>
      <i />
      {children}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  compact = false
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  compact?: boolean;
}) {
  return (
    <div className={clsx("empty-state", compact && "empty-state--compact")}>
      <div className="empty-state__icon">{icon ?? <Inbox size={21} />}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      {actionLabel && onAction && (
        <Button variant="secondary" size="sm" onClick={onAction} icon={<Plus size={15} />}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

export function Metric({
  label,
  value,
  meta,
  icon,
  onClick
}: {
  label: string;
  value: ReactNode;
  meta: string;
  icon: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button className="metric" onClick={onClick} type="button">
      <span className="metric__icon">{icon}</span>
      <span className="metric__body">
        <span className="metric__label">{label}</span>
        <strong>{value}</strong>
        <small>{meta}</small>
      </span>
      {onClick && <ArrowUpRight className="metric__arrow" size={16} />}
    </button>
  );
}

export function LoadingView() {
  return (
    <div className="loading-view">
      <div className="loading-mark">
        <LoaderCircle size={24} />
      </div>
      <strong>正在连接你的世界</strong>
      <span>读取事实记录与当前状态…</span>
    </div>
  );
}

